
/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { MatDialog } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { first, map } from "rxjs/operators";
import { ImageViewDialogComponent, ImageViewDialogData } from "@src/app/shared/dialogs/image-view-dialog/image-view-dialog.component";
import { AbstractHttpService } from "@src/app/shared/abstract-http.service";
import { ConfirmDialogService } from "@src/app/shared/dialogs/confirmDialog";
import { EditDialogComponent } from "@src/app/shared/dialogs/edit-dialog/edit-dialog.component";
import { AttachmentField } from "@src/app/shared/field/AttachmentField";
import { FieldMaker } from "@src/app/shared/field/FieldMaker";
import { FieldSet } from "@src/app/shared/form/field-set/field-set.component";
import { AttachmentService } from "@src/app/shared/form/file/attachment.service";
import { FormPicklistComponent } from "@src/app/shared/form/form-picklist/form-picklist.component";
import { FormTextAreaComponent } from "@src/app/shared/form/form-text-area/form-text-area.component";
import { FormTextComponent } from "@src/app/shared/form/form-text/form-text.component";
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { AbstractObject, TenantId, uuid } from "@src/app/model/abstract-object";
import { Omc } from '@src/app/model/Omc';

export class Attachment extends AbstractObject {
    constructor(o: Partial<Attachment> = {}) {
        super(o);
    }
    teamId?: TenantId;
    //txnId?: uuid;
    relatedId: uuid;
    size: number;
    ext: string;

    static download(attach: Attachment, svc: AbstractHttpService, forceTeam: uuid = null) {
        Attachment.fetchFile(attach, svc, forceTeam).subscribe(file => Attachment.saveToDisk(file));
    }

    static viewOrDownload(attach: Attachment, svc: AbstractHttpService, dialog: MatDialog, cds: ConfirmDialogService) {
        Attachment.fetchFile(attach, svc).subscribe(file => {
            if (file.type.startsWith('image/')) {
                Attachment.showImage(file, dialog);
            } else {
                Attachment.saveToDisk(file);
                cds.alert($localize`File Downloaded`, $localize`${file.name} has been downloaded`);
            }
        });
    }

    private static fetchFile(attach: Attachment, svc: AbstractHttpService, forceTeam: uuid = null): Observable<File> {
        return svc.getAttachment(attach.id, forceTeam).pipe(map(fileResponse => {
            const contentType = fileResponse.headers.get('our-mime-type');
            const fileName = fileResponse.headers.get('our-file-name');
            return new File([fileResponse.body], fileName, { type: contentType });
        }));
    }

    private static saveToDisk(file: File) {
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.style.display = "none";

        const fileURL = URL.createObjectURL(file);

        a.href = fileURL;
        a.download = file.name;
        a.click();
        window.URL.revokeObjectURL(fileURL);
    }

    private static showImage(file: File, dialog: MatDialog) {
        const imageUrl = URL.createObjectURL(file);
        const data: ImageViewDialogData = { imageUrl, fileName: file.name };
        dialog.open(ImageViewDialogComponent, { data, autoFocus: false, maxWidth: '100vw', maxHeight: '100vh' })
            .afterClosed().subscribe(() => URL.revokeObjectURL(imageUrl));
    }

    static editAttachment(attachment: Attachment, dialog: MatDialog, field: AttachmentField,
        attachSvc: AttachmentService, omcs: Omc[] = []) {
        const fields = [];
        if (field.fileOpts.allowMove) {
            fields.push(FormPicklistComponent.make($localize`OMC Name`, 'teamId', 'team',
                { items: omcs, optionValue: o => o.omcTeamId },
                { cellOpts: { minWidth: '30px', maxWidth: '120px' } }
            ));
        }

        fields.push(FieldMaker.id());
        fields.push(FieldMaker.rev());
        fields.push(FormTextComponent.make('Name', 'name'));
        fields.push(FormTextAreaComponent.make('Notes', 'notes'));

        const dialogFormConfig = new FormConfig(
            {
                title: $localize`Attachment`,
                fieldSet: new FieldSet(
                    {
                        fields,
                        formLayout: [{ cells: [{}] }]
                    }
                ),
            }
        );

        const dialogRef = dialog.open(EditDialogComponent,
            {
                data:
                {
                    config: dialogFormConfig,
                    service: attachSvc,
                    id: attachment.id,
                    hideTabs: true
                }
            }
        );

        dialogRef.afterClosed().pipe(first()).pipe<Attachment>(map(o => {
            if (o === 1) { // Gone to another OMC/Team
                field.removeAttachment(attachment);
            } else {
                if (o) {
                    attachment.name = o.name;
                    attachment.notes = o.notes;
                    attachment.revision = o.revision;
                }
            }
            return o;
        })).subscribe();
    }

    static deleteAttachment(attachment: Attachment, cds: ConfirmDialogService, field: AttachmentField) {
        cds.open($localize`Delete File`,
            $localize`Delete ${attachment.name}?`,
            () => {
                field.fileOpts.service.detach(attachment).subscribe((o: Attachment) => {
                    if (o) {
                        field.removeAttachment(attachment);
                    }
                })
            }
        );
    }
}
